import React from 'react';
import { motion } from 'framer-motion';
import { BrainIcon, TargetIcon, WrenchIcon, XIcon, CheckIcon } from 'lucide-react';
export function Approach() {
  const pillars = [{
    title: 'The Whole Person',
    description: 'I look beyond just the injury to understand how your sleep, stress, lifestyle habits, and mindset all play a role in your recovery. This bigger-picture approach helps me find the real drivers of your pain — not just the symptoms.',
    icon: <BrainIcon size={28} className="text-amber-500" data-id="element-45" />
  }, {
    title: 'Goal-Driven Programming',
    description: 'For athletes, I program with the end goal in mind — making sure your rehab progressively exposes you to the demands of your sport or activity. Every exercise has a purpose tied to getting you back to what you love, stronger than before.',
    icon: <TargetIcon size={28} className="text-amber-500" data-id="element-46" />
  }, {
    title: 'Practical & Individualized',
    description: "No cookie-cutter protocols. I build plans around the resources you actually have — your gym, your schedule, your equipment. The result is a program that fits your life and is something you'll actually follow through on.",
    icon: <WrenchIcon size={28} className="text-amber-500" data-id="element-47" />
  }];
  const traditionalItems = ['2–3 visits per week, 15–20 minutes with your PT', 'Passed off to an aide for most of your session', 'Mostly passive treatments: ice, heat, ultrasound, e-stim, massage', 'Cookie-cutter exercise plan with no real progression', 'Zero communication between sessions', 'A cycle of no progress, dropout, and returning to the same pain'];
  const mcleanItems = ["Focused 1-on-1 sessions to understand what's driving your symptoms", 'Comprehensive evaluation, objective testing, and hands-on coaching to guide your progress', 'Exercise-based care with intentional progression', 'Custom programming delivered straight to your phone', 'Messaging support and form review between visits', 'A clear path from pain to performance—and long-term independence'];
  return <section id="approach" className="py-24 bg-charcoal-950 text-offwhite-50" data-id="element-48">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-49">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-50">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-51">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-52">My Approach</h2>
            <p className="text-xl text-offwhite-200 mb-6 leading-relaxed" data-id="element-53">
              Every plan starts with the end in mind. I build each program
              around your goals, your resources, and your preferences—guided by
              evidence-based principles.
            </p>
            <p className="text-lg text-offwhite-200 leading-relaxed" data-id="element-54">
              By combining in-person sessions for hands-on assessment and
              treatment with custom remote programming delivered to your phone,
              you get a truly individualized plan that works on the days we see
              each other — and every day in between.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-charcoal-900 p-8 md:p-10 rounded-3xl border border-charcoal-800 relative overflow-hidden" data-id="element-55">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-56"></div>
            <h3 className="text-2xl font-bold mb-8 relative z-10" data-id="element-57">
              How It Works
            </h3>
            <div className="space-y-8 relative z-10" data-id="element-58">
              <div className="flex gap-4" data-id="element-59">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center font-bold" data-id="element-60">
                  1
                </div>
                <div data-id="element-61">
                  <h4 className="text-lg font-bold mb-1" data-id="element-62">
                    In-Person or Virtual Evaluation
                  </h4>
                  <p className="text-offwhite-200 text-sm" data-id="element-63">
                    We start with a deep dive into your history, current
                    limitations, and goals to understand the full picture and
                    create the right plan.
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-64">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center font-bold" data-id="element-65">
                  2
                </div>
                <div data-id="element-66">
                  <h4 className="text-lg font-bold mb-1" data-id="element-67">
                    Custom App Programming
                  </h4>
                  <p className="text-offwhite-200 text-sm" data-id="element-68">
                    Your rehab and training plan delivered straight to your
                    phone.
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-69">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center font-bold" data-id="element-70">
                  3
                </div>
                <div data-id="element-71">
                  <h4 className="text-lg font-bold mb-1" data-id="element-72">
                    Strategic Check-ins
                  </h4>
                  <p className="text-offwhite-200 text-sm" data-id="element-73">
                    In-person or virtual follow-ups to progress your plan and
                    ensure results.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mb-24" data-id="element-74">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-75">
            Not All PT Is Created Equal
          </h3>
          <p className="text-offwhite-200 text-center text-lg mb-12 max-w-2xl mx-auto" data-id="element-76">
            If your rehab feels like a revolving door, it's not you — it's the
            model.
          </p>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-77">
            {/* Traditional */}
            <div className="bg-charcoal-900 rounded-2xl border border-charcoal-800 p-8 md:p-10" data-id="element-78">
              <div className="flex items-center gap-3 mb-8" data-id="element-79">
                <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center" data-id="element-80">
                  <XIcon size={20} className="text-red-400" data-id="element-81" />
                </div>
                <h4 className="text-xl font-bold text-offwhite-100" data-id="element-82">
                  Traditional Rehab
                </h4>
              </div>
              <ul className="space-y-5" data-id="element-83">
                {traditionalItems.map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.1
              }} className="flex items-start gap-3" data-id="element-84">
                    <XIcon size={18} className="text-red-400/60 flex-shrink-0 mt-1" data-id="element-85" />
                    <span className="text-offwhite-200/70" data-id="element-86">{item}</span>
                  </motion.li>)}
              </ul>
            </div>

            {/* McLean PT */}
            <div className="bg-charcoal-900 rounded-2xl border border-amber-500/30 p-8 md:p-10 relative overflow-hidden" data-id="element-87">
              <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl -mr-12 -mt-12" data-id="element-88"></div>
              <div className="flex items-center gap-3 mb-8 relative z-10" data-id="element-89">
                <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center" data-id="element-90">
                  <CheckIcon size={20} className="text-amber-500" data-id="element-91" />
                </div>
                <h4 className="text-xl font-bold text-offwhite-50" data-id="element-92">
                  McLean PT
                </h4>
              </div>
              <ul className="space-y-5 relative z-10" data-id="element-93">
                {mcleanItems.map((item, i) => <motion.li key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.1
              }} className="flex items-start gap-3" data-id="element-94">
                    <CheckIcon size={18} className="text-amber-500 flex-shrink-0 mt-1" data-id="element-95" />
                    <span className="text-offwhite-100" data-id="element-96">{item}</span>
                  </motion.li>)}
              </ul>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-10" data-id="element-97">
          {pillars.map((pillar, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.2
        }} className="bg-charcoal-900/50 p-8 rounded-2xl border border-charcoal-800" data-id="element-98">
              <div className="mb-6" data-id="element-99">{pillar.icon}</div>
              <h3 className="text-xl font-bold mb-3" data-id="element-100">{pillar.title}</h3>
              <p className="text-offwhite-200 leading-relaxed" data-id="element-101">
                {pillar.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}